import React, { CSSProperties, forwardRef, useMemo, useRef, useImperativeHandle } from "react";
import { Avatar as PrimeAvatar, AvatarProps as PrimeAvatarProps } from "primereact/avatar";
import { getColorBaseTokenValue, mergeClassNames } from "../../design-tokens";
import styles from "./Avatar.module.css";

/**
 * Tipo semântico do avatar
 * - person: Representa pessoas (shape circle)
 * - entity: Representa entidades técnicas/sistemas (shape square)
 */
export type AvatarType = "person" | "entity";

/**
 * Props do componente Avatar
 */
export interface AvatarProps
  extends Omit<PrimeAvatarProps, "shape" | "size" | "label" | "pt" | "ptOptions"> {
  /**
   * Tipo semântico do avatar
   * - person: circle (pessoas)
   * - entity: square (máquinas, sistemas)
   */
  type: AvatarType;

  /**
   * Nome para gerar iniciais
   * Usado quando não há imagem
   */
  name?: string;

  /**
   * URL da imagem do avatar
   */
  image?: string;
}

/**
 * Extrai iniciais de um nome (máximo 2 caracteres)
 */
const getInitials = (name: string): string => {
  const words = name.trim().split(/\s+/);
  
  if (words.length === 1) {
    // Uma palavra: primeira letra maiúscula
    return words[0].charAt(0).toUpperCase();
  }
  
  // Duas ou mais palavras: primeira letra de cada uma (máx 2)
  return words
    .slice(0, 2)
    .map(word => word.charAt(0).toUpperCase())
    .join("");
};

/**
 * Cria variáveis CSS para avatar
 */
const createStyleVariables = (image?: string): CSSProperties => {
  // Se tem imagem, não precisa de cor de fundo
  if (image) {
    return {};
  }

  // Usa cor secundária como padrão
  return {
    "--ds-avatar-bg": getColorBaseTokenValue("color.secondary.500"),
    "--ds-avatar-color": getColorBaseTokenValue("color.neutral.000"),
  } as CSSProperties;
};

/**
 * Avatar Component
 * 
 * Representa pessoas (circle) ou entidades técnicas (square) usando imagens
 * ou iniciais geradas automaticamente.
 * 
 * @example
 * // Avatar de pessoa com nome
 * <Avatar type="person" name="João Silva" />
 * 
 * @example
 * // Avatar de pessoa com imagem
 * <Avatar type="person" name="João Silva" image="https://..." />
 * 
 * @example
 * // Avatar de entidade (máquina/sistema)
 * <Avatar type="entity" name="Linha 01" />
 */
export const Avatar = forwardRef<HTMLDivElement, AvatarProps>(
  (
    {
      type,
      name,
      image,
      className,
      style,
      ...rest
    },
    ref
  ) => {
    // Ref interno para capturar o elemento DOM do PrimeAvatar
    const avatarRef = useRef<any>(null);

    // Expõe o elemento DOM através do ref externo
    useImperativeHandle(ref, () => {
      return avatarRef.current?.getElement?.() || avatarRef.current;
    }, []);

    // Deriva shape do type
    const shape = type === "person" ? "circle" : "square";

    // Gera label (iniciais) se tem nome e não tem imagem
    const label = useMemo(() => {
      if (image || !name) return undefined;
      return getInitials(name);
    }, [name, image]);

    // Monta classes CSS
    const classNames = mergeClassNames(
      styles.root,
      styles[type],
      className
    );

    // Cria variáveis CSS
    const styleVariables = useMemo<CSSProperties>(
      () => ({
        ...createStyleVariables(image),
        ...style
      }),
      [image, style]
    );

    return (
      <PrimeAvatar
        {...rest}
        ref={avatarRef}
        shape={shape}
        image={image}
        label={label}
        className={classNames}
        style={styleVariables}
        data-ds-type={type}
      />
    );
  }
);

Avatar.displayName = "Avatar";

export default Avatar;
